import React from 'react';
import { motion } from 'framer-motion';
import { getTemplate, getTemplateIds } from '../../styles/imageTemplates';

/**
 * StyleSelector - 風格選擇器組件 (修復版)
 * 
 * 修復：適配新的極簡模板結構
 */
const StyleSelector = ({ selectedTemplate, onTemplateChange, language }) => {
  const templateIds = getTemplateIds();

  return (
    <div className="w-full">
      {/* Template Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {templateIds.map((templateId) => {
          const template = getTemplate(templateId);
          const isSelected = selectedTemplate === templateId;

          return (
            <motion.button
              key={templateId}
              onClick={() => onTemplateChange(templateId)}
              className={`relative flex flex-col items-center p-3 rounded-xl transition-all ${
                isSelected 
                  ? 'ring-2 ring-love-500 bg-love-50 dark:bg-love-900/30 shadow-lg' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              {/* Selection Indicator */}
              {isSelected && (
                <motion.div
                  className="absolute -top-2 -right-2 w-6 h-6 bg-love-500 rounded-full flex items-center justify-center shadow-lg z-10"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 20 }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path 
                      d="M20 6L9 17l-5-5" 
                      stroke="white" 
                      strokeWidth="2.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.div>
              )}

              {/* Template Preview Circle */}
              <div 
                className={`w-14 h-14 rounded-full mb-2 flex items-center justify-center shadow-md relative overflow-hidden border-2 ${
                  isSelected ? 'border-love-400' : 'border-white/60'
                }`}
                style={{ background: template.colors.mainBg }}
              >
                {/* Heart Icon */}
                <div className="relative z-10">
                  <svg 
                    width="28" 
                    height="28" 
                    viewBox="0 0 24 24" 
                    fill="none"
                    style={{
                      filter: `drop-shadow(0 2px 8px ${template.colors.accent}80) drop-shadow(0 1px 2px rgba(0,0,0,0.3))`
                    }}
                  >
                    <path 
                      d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" 
                      fill={template.colors.accent}
                      stroke="rgba(255,255,255,0.8)"
                      strokeWidth="0.5"
                    />
                  </svg>
                </div>
              </div>

              {/* Template Name */}
              <div 
                className={`text-xs font-medium text-center leading-tight transition-colors ${
                  isSelected 
                    ? 'text-love-700 dark:text-love-300' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
                style={{
                  wordBreak: 'break-word',
                  maxWidth: '100%'
                }}
              >
                {language === 'zh' ? template.nameZh : template.nameEn}
              </div>

              {/* Template Emoji */}
              <div className="text-xs mt-1 opacity-70">
                {template.emoji}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Style Description */}
      {selectedTemplate && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center bg-gray-50 dark:bg-gray-800 p-3 rounded-lg"
        >
          {language === 'zh' ? 
            getTemplate(selectedTemplate).description?.zh || '精美風格模板' :
            getTemplate(selectedTemplate).description?.en || 'Beautiful style template'
          }
        </motion.div>
      )}
    </div>
  );
};

export default StyleSelector;